import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from './PlatformIcon';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import type { SocialPlatform } from './SocialConnectButton';

interface AutoPostSettings {
  enabled: boolean;
  publish_mode: 'auto' | 'approval';
  frequency_days: number;
  preferred_hour: number;
  guidelines: string;
  next_run_at: string | null;
  last_posted_at: string | null;
}

const DEFAULT_POST: AutoPostSettings = {
  enabled: false,
  publish_mode: 'approval',
  frequency_days: 7,
  preferred_hour: 10,
  guidelines: '',
  next_run_at: null,
  last_posted_at: null,
};

function guidelinesOk(text: string) {
  return text.trim().length >= 10;
}

function resolveTimeZone(businessTz?: string | null): string {
  if (businessTz && String(businessTz).trim()) return String(businessTz).trim();
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function computeNextRunIso(frequencyDays: number, preferredHour: number, timeZone: string): string {
  const days = Math.max(7, frequencyDays);
  const now = new Date();
  const guess = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  // Approximate preferred hour in local TZ by setting UTC then adjusting via label — good enough for UI.
  guess.setHours(preferredHour, 0, 0, 0);
  void timeZone;
  return guess.toISOString();
}

interface SocialAutomateSettingsProps {
  platform: SocialPlatform;
  connectionId: string;
  displayName?: string;
  onBack: () => void;
}

export default function SocialAutomateSettings({
  platform,
  connectionId,
  displayName,
  onBack,
}: SocialAutomateSettingsProps) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [posts, setPosts] = useState<AutoPostSettings>(DEFAULT_POST);

  const label =
    platform === 'facebook' ? 'Facebook' : platform === 'linkedin' ? 'LinkedIn' : 'Instagram';
  const Icon =
    platform === 'facebook' ? FacebookIcon : platform === 'linkedin' ? LinkedInIcon : InstagramIcon;
  const tz = resolveTimeZone((selectedBusiness as { timezone?: string } | null)?.timezone);

  const load = useCallback(async () => {
    if (!businessId || !connectionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await supabase
        .from('social_auto_post_settings')
        .select('*')
        .eq('connection_id', connectionId)
        .maybeSingle();
      if (data) {
        setPosts({
          enabled: !!data.enabled,
          publish_mode: data.publish_mode === 'auto' ? 'auto' : 'approval',
          frequency_days: Number(data.frequency_days) || 7,
          preferred_hour: Number(data.preferred_hour) || 10,
          guidelines: data.guidelines || '',
          next_run_at: data.next_run_at,
          last_posted_at: data.last_posted_at,
        });
      } else {
        setPosts(DEFAULT_POST);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, [businessId, connectionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePosts = async () => {
    if (!businessId || !connectionId) {
      toast.error(`Connect ${label} first`);
      return;
    }
    if (posts.enabled && !guidelinesOk(posts.guidelines)) {
      toast.error('Add guidelines (at least 10 characters) before enabling auto posts');
      return;
    }

    const frequency_days = Math.min(90, Math.max(7, Number(posts.frequency_days) || 7));
    const preferred_hour = Math.min(23, Math.max(0, Number(posts.preferred_hour) || 10));
    setSaving(true);
    try {
      const next_run_at = posts.enabled
        ? posts.next_run_at || computeNextRunIso(frequency_days, preferred_hour, tz)
        : null;
      const enabled = posts.enabled && guidelinesOk(posts.guidelines);

      const { error } = await supabase.from('social_auto_post_settings').upsert(
        {
          business_id: businessId,
          connection_id: connectionId,
          enabled,
          publish_mode: 'auto',
          frequency_days,
          preferred_hour,
          guidelines: posts.guidelines.trim(),
          next_run_at,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'connection_id' },
      );
      if (error) throw error;

      setPosts((prev) => ({
        ...prev,
        enabled,
        frequency_days,
        preferred_hour,
        next_run_at,
      }));

      if (enabled) {
        const { data: fillData, error: fillError } = await supabase.functions.invoke(
          'social-auto-post-action',
          { body: { action: 'fill_queue', businessId, connectionId, platform } },
        );
        if (fillError || fillData?.error) {
          toast.success('Settings saved — draft queue will fill shortly');
        } else {
          const created = fillData?.created ?? 0;
          const pending = fillData?.pending ?? 0;
          toast.success(
            created > 0
              ? `Settings saved · ${created} draft${created === 1 ? '' : 's'} added (${pending} ready)`
              : `Settings saved · ${pending} draft${pending === 1 ? '' : 's'} ready`,
          );
        }
      } else {
        toast.success('Auto-post settings saved');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save auto-post settings');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!businessId) return;
    if (!guidelinesOk(posts.guidelines)) {
      toast.error('Write guidelines first so the AI knows your brand voice');
      return;
    }
    setPreviewing(true);
    setPreviewText(null);
    try {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'preview',
          businessId,
          connectionId,
          platform,
          guidelines: posts.guidelines,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || 'Failed to generate preview');
        return;
      }
      setPreviewText(data.content);
      toast.success(`Preview generated (${data.tokensUsed} tokens)`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 touch-manipulation"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        ← Back to Automations
      </button>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm overflow-hidden">
          <Icon className="h-9 w-9" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
            {displayName || label} automations
          </h2>
          <p className="text-sm text-gray-500">
            AI posts for {label} use your AI token balance
            {platform === 'instagram' ? ' · Instagram posts require an image' : ''}
          </p>
        </div>
      </div>

      <section className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Auto posts</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            AI keeps up to 5 drafts ready
            {platform === 'instagram'
              ? ' using images from your Media library (each image used once)'
              : ' — mixing text and Media library images when available'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Guidelines <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Required. Tone, topics to cover, things to avoid, etc.
          </p>
          <textarea
            value={posts.guidelines}
            onChange={(e) => setPosts((p) => ({ ...p, guidelines: e.target.value }))}
            rows={5}
            placeholder={`Example: Friendly local tone for ${label}. Mention your core services naturally. Never invent discounts. Soft CTA to book.`}
            className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Enable auto posts</p>
            <p className="text-xs text-gray-500">Blocked until guidelines are filled in</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={posts.enabled}
            onClick={() => {
              if (!posts.enabled && !guidelinesOk(posts.guidelines)) {
                toast.error('Write guidelines before enabling');
                return;
              }
              setPosts((p) => ({ ...p, enabled: !p.enabled }));
            }}
            className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
              posts.enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow mt-1 transition-transform ${
                posts.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <p className="text-xs text-gray-500">
          Stages the next 5 posts and publishes each one on its scheduled day. You can edit or remove a staged post any time before then.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Frequency (days)</label>
            <input
              type="number"
              min={7}
              max={90}
              value={posts.frequency_days}
              onChange={(e) =>
                setPosts((p) => ({ ...p, frequency_days: Number(e.target.value) || 7 }))
              }
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Preferred hour</label>
            <input
              type="number"
              min={0}
              max={23}
              value={posts.preferred_hour}
              onChange={(e) =>
                setPosts((p) => ({ ...p, preferred_hour: Number(e.target.value) || 10 }))
              }
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void savePosts()}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save auto posts
          </button>
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={previewing}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-800 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Preview post
          </button>
        </div>

        {previewText && (
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm text-gray-800 whitespace-pre-wrap">
            {previewText}
          </div>
        )}
      </section>
    </div>
  );
}
